import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { selfAttendanceAPI, SelfAttendanceRecord } from '../api/selfAttendance';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import {
  Camera,
  MapPin,
  Clock,
  Calendar,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Eye,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight,
  User,
  ShieldCheck,
  Check,
} from 'lucide-react';
import { formatWorkingDuration, resolveUploadUrl } from '../utils/helpers';

export default function StaffSelfAttendancePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { staff: currentStaff, logout } = useAuthStore();

  // Camera & selfie states
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [isCameraLoading, setIsCameraLoading] = useState<boolean>(false);

  // GPS states
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locationName, setLocationName] = useState<string>('');
  const [isLocationLoading, setIsLocationLoading] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string>('');

  // Live clock state
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // History Month navigation (YYYY-MM)
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  // Modal for full photo view
  const [viewPhotoUrl, setViewPhotoUrl] = useState<string | null>(null);

  // 1. Fetch today's status
  const { data: todayRes, isLoading: isTodayLoading } = useQuery({
    queryKey: ['myTodayAttendance'],
    queryFn: () => selfAttendanceAPI.getMyToday(),
    refetchInterval: 15000,
  });

  // Extract the today record from the API response payload { staff, today, date }
  const todayRecord: SelfAttendanceRecord | null = todayRes?.data?.today || null;

  // Determine current status
  const isCheckedIn = Boolean(todayRecord && todayRecord.check_in_time);
  const isCheckedOut = Boolean(todayRecord && todayRecord.check_out_time);

  // 2. Fetch monthly history
  const { data: historyRes, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['myAttendanceHistory', selectedMonth],
    queryFn: () => selfAttendanceAPI.getMyHistory({ month: selectedMonth }),
  });
  const historyList = (historyRes?.data || []) as SelfAttendanceRecord[];

  // Live Clock Tick
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const timeStr = currentTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  const dateStr = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  // Attach video stream when camera activates
  const setVideoRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (node && stream) {
      node.srcObject = stream;
      node.play().catch((err) => console.error('Video play error:', err));
    }
  }, [stream]);

  // 3. Acquire live GPS location & Reverse Geocode
  const fetchLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setIsLocationLoading(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGpsCoords({ lat, lng });

        // Reverse Geocoding with OpenStreetMap Nominatim
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`,
            { headers: { 'Accept-Language': 'en' } }
          );
          if (res.ok) {
            const data = await res.json();
            const addr = data.address || {};
            const area =
              addr.suburb ||
              addr.neighbourhood ||
              addr.residential ||
              addr.commercial ||
              addr.road ||
              addr.village ||
              addr.town ||
              addr.city ||
              '';
            const city = addr.city || addr.state_district || addr.county || '';
            const state = addr.state || '';
            const postcode = addr.postcode || '';

            const parts = [area, city, state, postcode].filter(Boolean);
            const fullLoc = parts.length > 0 ? parts.join(', ') : data.display_name?.split(',').slice(0, 3).join(',') || '';
            setLocationName(fullLoc || `Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}`);
          } else {
            setLocationName(`Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}`);
          }
        } catch {
          setLocationName(`Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}`);
        } finally {
          setIsLocationLoading(false);
        }
      },
      (err) => {
        setIsLocationLoading(false);
        if (err.code === 1) {
          setLocationError('Location permission denied. Please allow location in browser settings.');
        } else if (err.code === 2) {
          setLocationError('Position unavailable. Please turn on GPS on your device.');
        } else {
          setLocationError('Location request timed out. Retrying...');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  // Trigger GPS auto-detection on component mount
  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  // 4. Camera Control
  const startCamera = async () => {
    setIsCameraLoading(true);
    try {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });

      setStream(mediaStream);
      setIsCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play().catch(() => {});
      }
    } catch (err: any) {
      console.error('Camera access error:', err);
      toast.error('Unable to open camera. Please grant camera permissions.');
    } finally {
      setIsCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  };

  const snapPhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Mirror for natural selfie orientation
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const base64 = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedPhoto(base64);
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera();
  };

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [stream]);

  // 5. Check-In & Check-Out Mutations
  const checkInMutation = useMutation({
    mutationFn: (payload: { lat: number; lng: number; photo: string; location_name?: string }) =>
      selfAttendanceAPI.checkIn(payload),
    onSuccess: (res: any) => {
      toast.success(res?.message || res?.data?.message || 'Check-In recorded successfully!');
      queryClient.invalidateQueries({ queryKey: ['myTodayAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['myAttendanceHistory'] });
      setCapturedPhoto(null);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error?.message || 'Failed to record Check-In.';
      toast.error(msg);
    },
  });

  const checkOutMutation = useMutation({
    mutationFn: (payload: { lat: number; lng: number; photo: string; location_name?: string }) =>
      selfAttendanceAPI.checkOut(payload),
    onSuccess: (res: any) => {
      toast.success(res?.message || res?.data?.message || 'Check-Out recorded successfully!');
      queryClient.invalidateQueries({ queryKey: ['myTodayAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['myAttendanceHistory'] });
      setCapturedPhoto(null);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error?.message || 'Failed to record Check-Out.';
      toast.error(msg);
    },
  });

  const handleSubmitAttendance = (type: 'check-in' | 'check-out') => {
    if (!capturedPhoto) {
      toast.error('Please capture your live selfie photo first.');
      return;
    }
    if (!gpsCoords) {
      toast.error('Location is required. Please enable GPS and click refresh location.');
      fetchLocation();
      return;
    }

    const payload = {
      lat: gpsCoords.lat,
      lng: gpsCoords.lng,
      photo: capturedPhoto,
      location_name: locationName || undefined,
    };

    if (type === 'check-in') {
      checkInMutation.mutate(payload);
    } else {
      checkOutMutation.mutate(payload);
    }
  };

  // Month navigation helpers
  const handlePrevMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const prevDate = new Date(y, m - 2, 1);
    const newY = prevDate.getFullYear();
    const newM = String(prevDate.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${newY}-${newM}`);
  };

  const handleNextMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const nextDate = new Date(y, m, 1);
    const newY = nextDate.getFullYear();
    const newM = String(nextDate.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${newY}-${newM}`);
  };

  const formatMonthLabel = (mStr: string) => {
    const [y, m] = mStr.split('-').map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Monthly stats calculation
  const totalDays = historyList.length;
  const presentDays = historyList.filter((r) => r.status === 'present').length;
  const lateDays = historyList.filter((r) => r.status === 'late').length;
  const totalHours = historyList.reduce((sum, r) => sum + (Number(r.working_hours) || 0), 0);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="w-full flex flex-col items-center select-none font-sans pb-8">
      {/* Hidden canvas for taking snapshot */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Main Container */}
      <div className="w-full max-w-3xl space-y-4">
        {/* Top Header Card */}
        <div className="bg-white dark:bg-[#121414] p-4 rounded-xl border border-gray-200 dark:border-white/10 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 rounded-lg bg-red-100 dark:bg-red-500/20 border border-red-200 dark:border-red-500/30 flex items-center justify-center text-red-700 dark:text-red-400 font-black text-base overflow-hidden shrink-0">
              {currentStaff?.profile_picture ? (
                <img
                  src={resolveUploadUrl(currentStaff.profile_picture)}
                  alt={currentStaff.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                currentStaff?.full_name?.charAt(0) || 'G'
              )}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-base font-black text-gray-950 dark:text-white tracking-tight">
                  {currentStaff?.full_name || 'Staff Member'}
                </h1>
                <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-500/30 font-mono font-black">
                  {currentStaff?.role || 'Staff'}
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 flex items-center space-x-1 mt-0.5 font-bold">
                <span className="font-mono">Code: {currentStaff?.staff_code || 'GOC'}</span>
                <span>•</span>
                <span>GOC Studio</span>
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 border border-gray-200 dark:border-white/15 text-gray-900 dark:text-gray-200 font-bold transition-all flex items-center space-x-1.5 text-xs cursor-pointer"
            title="Logout"
          >
            <LogOut className="w-3.5 h-3.5 text-rose-600" />
            <span>Logout</span>
          </button>
        </div>

        {/* Live Clock & Shift Status Hero Banner */}
        <div className="bg-white dark:bg-[#121414] p-4 rounded-xl border border-gray-200 dark:border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-white/10 pb-3">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-red-600 dark:text-red-400 font-bold flex items-center space-x-1.5">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-ping inline-block" />
                <span>Live Studio Clock</span>
              </div>
              <div className="text-2xl sm:text-3xl font-mono font-black text-gray-950 dark:text-white tracking-tight mt-1">
                {timeStr || '--:--:--'}
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-300 flex items-center space-x-1 mt-0.5 font-bold">
                <Calendar className="w-3.5 h-3.5 text-gray-500" />
                <span>{dateStr}</span>
              </div>
            </div>

            {/* Today's Status Badge */}
            <div className="sm:text-right">
              <span className="text-[11px] text-gray-600 dark:text-gray-400 block mb-1 font-bold uppercase tracking-wider">Today's Status</span>
              {isTodayLoading ? (
                <div className="text-xs text-gray-600 font-bold animate-pulse">Loading status...</div>
              ) : isCheckedOut ? (
                <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded bg-emerald-600 text-white text-xs font-black">
                  <CheckCircle2 className="w-3.5 h-3.5 text-white shrink-0" />
                  <span>Shift Completed ({formatWorkingDuration(todayRecord?.check_in_time, todayRecord?.check_out_time, todayRecord?.working_hours)})</span>
                </div>
              ) : isCheckedIn ? (
                <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded bg-amber-600 text-white text-xs font-black">
                  <Clock className="w-3.5 h-3.5 text-white shrink-0 animate-spin" style={{ animationDuration: '3s' }} />
                  <span>On Shift ({todayRecord?.status === 'late' ? 'Late In' : 'Present'})</span>
                </div>
              ) : (
                <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-gray-200 text-xs font-black border border-gray-300 dark:border-white/20">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span>Not Checked In Yet</span>
                </div>
              )}
            </div>
          </div>

          {/* Today's Summary Row (if checked in) */}
          {isCheckedIn && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-3 text-xs">
              <div className="bg-gray-50 dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10">
                <span className="text-gray-600 dark:text-gray-400 block text-[11px] font-bold">Check-In Time</span>
                <span className="font-black text-gray-950 dark:text-white font-mono text-sm">
                  {todayRecord?.check_in_time ? new Date(todayRecord.check_in_time.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '--'}
                </span>
                {todayRecord?.check_in_location_name && (
                  <p className="text-[11px] text-gray-900 dark:text-white font-bold truncate mt-0.5" title={todayRecord.check_in_location_name}>
                    📍 {todayRecord.check_in_location_name}
                  </p>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10">
                <span className="text-gray-600 dark:text-gray-400 block text-[11px] font-bold">Check-Out Time</span>
                <span className="font-black text-gray-950 dark:text-white font-mono text-sm">
                  {todayRecord?.check_out_time ? new Date(todayRecord.check_out_time.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'Pending'}
                </span>
                {todayRecord?.check_out_location_name && (
                  <p className="text-[11px] text-gray-900 dark:text-white font-bold truncate mt-0.5" title={todayRecord.check_out_location_name}>
                    📍 {todayRecord.check_out_location_name}
                  </p>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10 col-span-2 sm:col-span-1">
                <span className="text-gray-600 dark:text-gray-400 block text-[11px] font-bold">Working Duration</span>
                <span className="font-black text-red-600 dark:text-red-400 font-mono text-sm">
                  {isCheckedOut
                    ? formatWorkingDuration(todayRecord?.check_in_time, todayRecord?.check_out_time, todayRecord?.working_hours)
                    : isCheckedIn
                    ? 'In Progress'
                    : '--'}
                </span>
                {todayRecord?.check_in_distance_meters !== null && todayRecord?.check_in_distance_meters !== undefined && (
                  <p className={`text-[11px] mt-0.5 font-black ${todayRecord.check_in_distance_meters <= 30 ? 'text-emerald-700 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'}`}>
                    Studio: {todayRecord.check_in_distance_meters < 1000 ? `${todayRecord.check_in_distance_meters}m` : `${(todayRecord.check_in_distance_meters / 1000).toFixed(1)}km`} ({todayRecord.check_in_distance_meters <= 30 ? 'On-Site' : 'Off-Site (Admin)'})
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Panel: Selfie Camera & GPS Verification */}
        {(!isCheckedIn || !isCheckedOut) ? (
          <div className="bg-white dark:bg-[#121414] p-4 rounded-xl border border-gray-200 dark:border-white/10 space-y-3.5">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 pb-2.5">
              <div className="flex items-center space-x-2">
                <div className={`p-1.5 rounded-lg border ${!isCheckedIn ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                  {!isCheckedIn ? <Camera className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
                </div>
                <div>
                  <h2 className="font-black text-gray-950 dark:text-white text-sm sm:text-base">
                    {!isCheckedIn ? '1. Mark Today Check-In' : '2. Mark Today Check-Out'}
                  </h2>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                    {!isCheckedIn
                      ? 'Live selfie within 30m of GOC Studio required (Cutoff: 10:15 AM IST)'
                      : 'Live selfie within 30m of GOC Studio to confirm end of shift'}
                  </p>
                </div>
              </div>
              <span className="text-xs font-bold text-gray-600 dark:text-gray-300">Live Selfie & GPS</span>
            </div>

            {/* Camera View Box */}
            <div className="relative aspect-[4/3] w-full max-w-sm mx-auto bg-gray-900 rounded-xl overflow-hidden border-2 border-gray-300 dark:border-white/20 flex items-center justify-center">
              {capturedPhoto ? (
                <div className="relative w-full h-full">
                  <img
                    src={capturedPhoto}
                    alt="Captured Selfie"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 px-2.5 py-1 rounded bg-emerald-600 text-white text-xs font-black flex items-center space-x-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                    <span>Photo Captured</span>
                  </div>
                  <button
                    onClick={retakePhoto}
                    className="absolute bottom-2.5 right-2.5 px-3 py-1.5 rounded bg-black/90 hover:bg-black text-xs font-black text-white border border-white/40 transition-all flex items-center space-x-1.5 cursor-pointer"
                  >
                    <RefreshCw className="w-3.5 h-3.5 text-red-400" />
                    <span>Retake Photo</span>
                  </button>
                </div>
              ) : isCameraActive ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  <video
                    ref={setVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover scale-x-[-1]"
                  />
                  <div className="absolute bottom-3 inset-x-0 flex justify-center">
                    <button
                      onClick={snapPhoto}
                      className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 active:scale-95 text-white font-black text-xs sm:text-sm transition-all flex items-center space-x-2 border border-white/40 cursor-pointer"
                    >
                      <Camera className="w-4 h-4 text-white" />
                      <span>Take Photo</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center p-6 space-y-3">
                  <div className="w-14 h-14 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mx-auto text-white">
                    <Camera className="w-7 h-7 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-black text-white">
                      {!isCheckedIn ? 'Check-In Live Camera Selfie' : 'Check-Out Live Camera Selfie'}
                    </p>
                    <p className="text-xs text-gray-300 font-bold mt-0.5">Please take a live selfie to verify attendance</p>
                  </div>
                  <button
                    onClick={startCamera}
                    disabled={isCameraLoading}
                    className={`px-4 py-2.5 rounded-lg text-white text-xs font-black transition-all flex items-center space-x-1.5 mx-auto cursor-pointer ${
                      !isCheckedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
                    }`}
                  >
                    {isCameraLoading ? (
                      <span>Opening Camera...</span>
                    ) : (
                      <>
                        <Camera className="w-3.5 h-3.5 text-white" />
                        <span>Open Camera</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* GPS Location Auto-Detection Bar */}
            <div className="bg-gray-50 dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10 space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-black text-gray-950 dark:text-white flex items-center space-x-1.5">
                  <MapPin className="w-4 h-4 text-red-600" />
                  <span>Live Location Verification</span>
                </span>
                <button
                  onClick={fetchLocation}
                  disabled={isLocationLoading}
                  className="text-red-600 dark:text-red-400 hover:underline text-[11px] font-black flex items-center space-x-1 cursor-pointer"
                >
                  <RefreshCw className={`w-3 h-3 ${isLocationLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh GPS</span>
                </button>
              </div>

              {isLocationLoading ? (
                <div className="text-xs text-gray-700 dark:text-gray-300 font-bold animate-pulse flex items-center space-x-1">
                  <span>Acquiring high-accuracy GPS coordinates...</span>
                </div>
              ) : locationError ? (
                <div className="text-xs text-rose-700 dark:text-rose-400 font-bold flex items-center space-x-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{locationError}</span>
                </div>
              ) : gpsCoords ? (
                <div className="space-y-0.5">
                  <p className="text-xs text-gray-950 dark:text-white font-bold" style={{ color: '#09090b' }}>
                    {locationName || 'Location detected'}
                  </p>
                  <p className="text-[11px] font-mono text-gray-600 dark:text-gray-400 font-bold">
                    GPS: {gpsCoords.lat.toFixed(6)}, {gpsCoords.lng.toFixed(6)}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-700 dark:text-gray-300 font-bold">Click "Refresh GPS" to detect current location</p>
              )}
            </div>

            {/* Submit Action Button */}
            {!isCheckedIn ? (
              capturedPhoto ? (
                <button
                  onClick={() => handleSubmitAttendance('check-in')}
                  disabled={checkInMutation.isPending}
                  className="w-full py-3.5 rounded-lg font-black text-sm text-white bg-red-600 hover:bg-red-700 active:scale-[0.99] transition-all flex items-center justify-center space-x-2 cursor-pointer"
                >
                  {checkInMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin text-white" />
                      <span className="text-white">Submitting Check-In...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-white" />
                      <span className="text-white font-black tracking-wide text-sm">Confirm & Mark Check-In (Present)</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="w-full py-3 px-4 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-300 dark:border-white/15 text-center text-xs font-black text-gray-800 dark:text-gray-200 flex items-center justify-center space-x-2">
                  <Camera className="w-4 h-4 text-red-600" />
                  <span>Step 1: Open camera & take selfie above to enable Check-In</span>
                </div>
              )
            ) : !isCheckedOut ? (
              capturedPhoto ? (
                <button
                  onClick={() => handleSubmitAttendance('check-out')}
                  disabled={checkOutMutation.isPending}
                  className="w-full py-3.5 rounded-lg font-black text-sm text-white bg-amber-600 hover:bg-amber-700 active:scale-[0.99] transition-all flex items-center justify-center space-x-2 cursor-pointer"
                >
                  {checkOutMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin text-white" />
                      <span className="text-white">Submitting Check-Out...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-5 h-5 text-white" />
                      <span className="text-white font-black tracking-wide text-sm">Confirm & Mark Check-Out</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="w-full py-3 px-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-500/30 text-center text-xs font-black text-amber-900 dark:text-amber-300 flex items-center justify-center space-x-2">
                  <Camera className="w-4 h-4 text-amber-600" />
                  <span>Step 2: Open camera & take selfie above to Confirm Check-Out</span>
                </div>
              )
            ) : null}
          </div>
        ) : (
          /* Shift Already Finished Message */
          <div className="bg-white dark:bg-[#121414] p-5 rounded-xl border border-gray-200 dark:border-white/10 text-center space-y-2.5">
            <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-base font-black text-gray-950 dark:text-white">Today's Attendance Completed!</h3>
            <p className="text-xs text-gray-700 dark:text-gray-300 max-w-md mx-auto font-bold">
              You have completed both check-in and check-out for today ({formatWorkingDuration(todayRecord?.check_in_time, todayRecord?.check_out_time, todayRecord?.working_hours)} recorded).
            </p>
          </div>
        )}

        {/* Monthly Attendance History Section */}
        <div className="bg-white dark:bg-[#121414] p-4 rounded-xl border border-gray-200 dark:border-white/10 space-y-3.5">
          {/* Header & Month Navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 dark:border-white/10 pb-3">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-red-600" />
              <h3 className="font-black text-gray-950 dark:text-white text-sm sm:text-base">My Attendance History</h3>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevMonth}
                className="p-1.5 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-900 dark:text-gray-200 transition-all border border-gray-200 dark:border-white/15 cursor-pointer"
                title="Previous Month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-black text-gray-950 dark:text-white font-mono px-3 py-1.5 bg-gray-100 dark:bg-white/10 rounded-md border border-gray-200 dark:border-white/15 min-w-[130px] text-center">
                {formatMonthLabel(selectedMonth)}
              </span>
              <button
                onClick={handleNextMonth}
                className="p-1.5 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-900 dark:text-gray-200 transition-all border border-gray-200 dark:border-white/15 cursor-pointer"
                title="Next Month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Monthly KPI Summary Cards - Clean Flat Pure White Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
            <div className="bg-white dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10">
              <span className="text-gray-600 dark:text-gray-400 block text-[11px] font-bold">Days Logged</span>
              <span className="font-black text-gray-950 dark:text-white font-mono text-base mt-0.5 block">{totalDays}</span>
            </div>
            <div className="bg-white dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10">
              <span className="text-gray-700 dark:text-gray-300 block text-[11px] font-bold">Present</span>
              <span className="font-black text-emerald-700 dark:text-emerald-400 font-mono text-base mt-0.5 block">{presentDays}</span>
            </div>
            <div className="bg-white dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10">
              <span className="text-gray-700 dark:text-gray-300 block text-[11px] font-bold">Late</span>
              <span className="font-black text-amber-700 dark:text-amber-400 font-mono text-base mt-0.5 block">{lateDays}</span>
            </div>
            <div className="bg-white dark:bg-white/5 p-3 rounded-lg border border-gray-200 dark:border-white/10">
              <span className="text-gray-700 dark:text-gray-300 block text-[11px] font-bold">Total Duration</span>
              <span className="font-black text-red-600 dark:text-red-400 font-mono text-base mt-0.5 block">{formatWorkingDuration(null, null, totalHours)}</span>
            </div>
          </div>

          {/* History Records List */}
          {isHistoryLoading ? (
            <div className="text-center py-8 text-xs text-gray-600 font-bold animate-pulse">
              Loading attendance history...
            </div>
          ) : historyList.length === 0 ? (
            <div className="text-center py-8 text-xs text-gray-700 dark:text-gray-300 font-bold">
              No attendance records found for {formatMonthLabel(selectedMonth)}.
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar pr-1">
              {historyList.map((rec) => {
                const recDate = new Date(rec.date);
                const formattedRecDate = recDate.toLocaleDateString('en-US', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                });

                return (
                  <div
                    key={rec.id}
                    className="p-3 rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                  >
                    <div className="flex items-center space-x-3">
                      {/* Photo Thumbnail */}
                      {rec.check_in_photo ? (
                        <button
                          onClick={() => setViewPhotoUrl(resolveUploadUrl(rec.check_in_photo) || null)}
                          className="w-9 h-9 rounded-lg overflow-hidden border-2 border-gray-300 dark:border-white/20 shrink-0 hover:scale-105 transition-all relative group cursor-pointer bg-gray-100"
                          title="Click to view full photo"
                        >
                          <img
                            src={resolveUploadUrl(rec.check_in_photo)}
                            alt="Check-in Selfie"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.currentTarget;
                              if (rec.check_in_photo && !target.src.endsWith(rec.check_in_photo)) {
                                target.src = rec.check_in_photo;
                              }
                            }}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                            <Eye className="w-3.5 h-3.5 text-white" />
                          </div>
                        </button>
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-gray-200 dark:bg-white/10 border border-gray-300 dark:border-white/15 flex items-center justify-center shrink-0 text-gray-700">
                          <User className="w-4 h-4" />
                        </div>
                      )}

                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-black text-gray-950 dark:text-white font-mono">{formattedRecDate}</span>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-black uppercase text-white ${
                              rec.status === 'present'
                                ? 'bg-emerald-600'
                                : rec.status === 'late'
                                ? 'bg-amber-600'
                                : 'bg-rose-600'
                            }`}
                          >
                            {rec.status}
                          </span>
                        </div>
                        {rec.check_in_location_name && (
                          <p className="text-[11px] text-gray-900 dark:text-gray-100 font-bold truncate max-w-xs mt-0.5" style={{ color: '#09090b' }}>
                            📍 {rec.check_in_location_name}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 sm:text-right text-[11px]">
                      <div>
                        <span className="block text-gray-950 dark:text-white font-mono font-black">
                          In: {rec.check_in_time ? new Date(rec.check_in_time.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '--'}
                        </span>
                        <span className="block text-gray-950 dark:text-white font-mono font-black">
                          Out: {rec.check_out_time ? new Date(rec.check_out_time.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '--'}
                        </span>
                      </div>
                      <div className="min-w-[70px]">
                        <span className="block font-black text-red-600 dark:text-red-400 font-mono text-xs">
                          {formatWorkingDuration(rec.check_in_time, rec.check_out_time, rec.working_hours)}
                        </span>
                        <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400">Duration</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="text-center text-[11px] text-gray-600 dark:text-gray-400 pt-2 font-bold">
          GOC Studio • Staff Attendance Portal • Secured with GPS & Live Photo Verification
        </div>
      </div>

      {/* Full Photo Modal */}
      {viewPhotoUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setViewPhotoUrl(null)}
        >
          <div
            className="relative max-w-md w-full bg-white dark:bg-[#121414] border border-gray-200 dark:border-white/20 rounded-xl overflow-hidden p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 pb-2">
              <h3 className="text-sm font-black text-gray-950 dark:text-white">Attendance Verification Selfie</h3>
              <button
                onClick={() => setViewPhotoUrl(null)}
                className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-white/10 dark:hover:bg-white/20 text-gray-900 dark:text-white cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-black flex items-center justify-center">
              <img
                src={resolveUploadUrl(viewPhotoUrl)}
                alt="Verification Full"
                className="max-h-[70vh] w-full object-contain"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (viewPhotoUrl && !target.src.endsWith(viewPhotoUrl)) {
                    target.src = viewPhotoUrl;
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
